import { useState } from 'react';
import { PackageCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { describeApiError } from '@/lib/api-errors';
import { wrapUpRun } from '@/lib/runs-api';
import { runRef } from './run-status';

/**
 * SF-T-209: the task's Wrap up, in Run's place while its newest run is live: the agent finishes and opens or
 * updates the pull request, then the run's checks and review rounds follow. Maintainers (as Run). SF-T-302: on a
 * suggestions-first run, the suggestions nobody decided are marked No thanks first.
 */
export function WrapUpButton({
  runId,
  runNumber,
  project,
  wrapping = false,
  suggestionsFirst = false,
  onDone,
  wrapUp = wrapUpRun,
}: {
  runId: string;
  runNumber: number;
  project?: string;
  /** Wrap up was pressed already (or a suggestions-first run had nothing left to decide): the run is finishing. */
  wrapping?: boolean;
  /** SF-T-302: a suggestions-first run: Wrap up marks its undecided suggestions No thanks. */
  suggestionsFirst?: boolean;
  onDone?: () => void;
  /** Preview only (the /dev/ui gallery). */
  wrapUp?: typeof wrapUpRun;
}) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const res = await wrapUp(runId, { project });
    setBusy(false);
    if (!res.data) {
      toast.error(describeApiError(res, `Couldn't wrap ${runRef(runNumber)} up`));
      return;
    }
    toast.success(
      `Wrapping ${runRef(runNumber)} up: ${suggestionsFirst ? 'suggestions nobody decided are marked No thanks, and ' : ''}the agent opens the pull request, then the checks and review follow`
    );
    onDone?.();
  };
  return (
    <Button
      variant="outline"
      size="xs"
      className="mr-1"
      disabled={wrapping}
      loading={busy}
      onClick={() => void go()}
      data-hint={
        wrapping
          ? `${runRef(runNumber)} is wrapping up: its PR, checks and review`
          : suggestionsFirst
            ? `Finish ${runRef(runNumber)} now: suggestions nobody has decided are marked No thanks, the agent opens or updates the pull request, then the checks and review follow`
            : `Finish ${runRef(runNumber)}: the agent opens or updates the pull request, then the checks and review follow`
      }
    >
      <PackageCheck />
      {wrapping ? 'Wrapping up' : 'Wrap up'}
    </Button>
  );
}
